export const getSpellId = ({ name }) => name.toLowerCase().replaceAll(/[\s/]/g, '-').replaceAll(/[^a-z-]/g, '');

export const levelDisplay = (level) => (
    level === 0 ? 'cantrip'
    : `${level}${levelSuffix(level)} level`
);
const levelSuffix = (level) => {
    switch (level) {
        case 1: return 'st';
        case 2: return 'nd';
        default: return 'th';
    }
};

export const classNames = (classes, includeSubclasses=true) => [
    ...new Set(
        ([
            ...(classes?.fromClassList || []),
            ...(classes?.fromClassListVariant || []).filter(v => !(v.definedInSource.startsWith('UA'))),
            ...(includeSubclasses ? nonUASubclasses(classes).map(sc => sc.class) : [])
        ]).map(c => c.name)
    )
];

export const subclassNames = (classes) => [
    ...new Set(
        ([
            ...nonUASubclasses(classes).map(sc => `${sc.subclass.name} ${sc.class.name}`)
        ])
    )
];

const nonUASubclasses = (classes) => [
    ...(classes?.fromSubclass || []).filter(sc => !(sc.class.source.startsWith('UA') || sc.subclass.source.startsWith('UA') || sc.subclass.source.startsWith('PSA')))
];

const singleTimeDisplay = (time) => {
    return `${time.number} ${time.unit}${time.number !== 1 ? 's': ''} ${time?.condition || ''}`;
};

export const timeDisplay = (time) => {
    if (!time) return '';
    return time.map(t => singleTimeDisplay(t)).join('or ');
};

export const compactTimeDisplay = (spell) => {
    if (!spell?.time) return '';
    return `${spell?.time[0].number === 1 ? '' : spell?.time[0].number}${spell?.time[0].unit[0]}${spell?.time.length > 1 ? '*' : ''}`.toUpperCase();
};

export const rangeDisplay = (range) => (
    `${range?.distance?.amount || ''} ${range?.distance?.type || ''} ${range?.type === 'point' ? '' : range?.type}`
);

export const componentsDisplay = (components) => (
    [
        ...(components?.v ? ['V'] : []),
        ...(components?.s ? ['S'] : []),
        ...(!!components?.m ? ['M*'] : []),
    ].join(', ')
);

export const componentsCompactDisplay = (spell) => (
    [
        ...(spell?.components?.v ? ['v'] : []),
        ...(spell?.components?.s ? ['s'] : []),
        ...(!!spell?.components?.m ? ['m'] : []),
    ].join('')
);

export const materialComponent = (components) => {
    if (typeof components?.m === 'string') {
        return components?.m;
    } else {
        return components?.m?.text;
    }
};

const pastTense = {
    dispel: 'dispelled',
    trigger: 'triggered'
};

export const durationDisplay = (duration) => {
    if (!duration) return '';
    switch(duration[0]?.type) {
        case 'timed':
            return `${duration[0]?.duration?.amount || ''} ${duration[0]?.duration?.type}`;
        case 'permanent':
            return `Until ${duration[0]?.ends.map(e => pastTense[e]).join(' or ')}`;
        default:
            return 'Instantaneous';
    }
};

export const isConc = (spell) => !!spell?.duration.find(({concentration}) => !!concentration);
export const isRitual = (spell) => !!spell?.meta?.ritual;

export const applyAllFilters = (s, filters, settings) => (
    filterByClass(s, filters.class, settings.includeSubclasses) &&
    filterBySchool(s, filters.school) &&
    filterByLevel(s, filters.level) &&
    filterBySource(s, filters.source) &&
    filterByComponent(s, filters.component) &&
    filterByTags(s, filters.tag) &&
    filterByDamageType(s, filters.damageType) &&
    filterByAttack(s, filters.attack) &&
    filterByCastingTime(s, filters.castingTime) &&
    filterBySave(s, filters.save)
);

export const filterByClass = ({ classes }, classFilter, includeSubclasses) => !classFilter || classNames(classes, includeSubclasses).includes(classFilter);
export const filterBySchool = ({ school }, schoolFilter) => !schoolFilter || school === schoolFilter;
export const filterBySave = ({ savingThrow }, saveFilter) => !saveFilter || (savingThrow || []).includes(saveFilter);
export const filterByLevel = ({ level }, levelFilter) => !levelFilter?.length || levelFilter.includes(level);
export const filterBySource = ({ source }, sourceFilter) => !sourceFilter?.length || sourceFilter.includes(source);
export const filterByDamageType = ({ damageInflict }, damageTypeFilter) => (
    !damageTypeFilter?.length || !!damageTypeFilter.filter(dType => (damageInflict || []).includes(dType)).length
);
export const filterByComponent = ({ components }, componentFilter) => {
    if(!componentFilter?.positive?.length && !componentFilter?.negative?.length) {
        return true;
    } 

    const isPositive = k => componentFilter.positive?.includes(k);
    const isNegative = k => componentFilter.negative?.includes(k);

    return (
        (isPositive('V') ? !!components.v : (isNegative('V') ? !components.v : true))
        && (isPositive('S') ? !!components.s : (isNegative('S') ? !components.s : true))
        && (isPositive('M') ? !!components.m : (isNegative('M') ? !components.m : true))
        && (isPositive('G') ? !!components?.m?.cost : (isNegative('G') ? !components?.m?.cost : true))
        && (isPositive('C') ? !!components?.m?.consume : (isNegative('C') ? !components?.m?.consume : true))
    );
};

export const filterByTags = (spell, tagFilter) => {
    if(!tagFilter?.positive?.length && !tagFilter?.negative?.length) {
        return true;
    } 

    const isPositive = k => tagFilter.positive?.includes(k);
    const isNegative = k => tagFilter.negative?.includes(k);

    return (
        (isPositive('RITUAL') ? isRitual(spell) : (isNegative('RITUAL') ? !isRitual(spell) : true))
        && (isPositive('CONCENTRATION') ? isConc(spell) : (isNegative('CONCENTRATION') ? !isConc(spell) : true))
    );
};

export const filterByAttack = ({ spellAttack }, attackFilter) => {
    if (!attackFilter) return true;

    const k = attackFilter.slice(0,1);

    return (spellAttack || []).includes(k);
};

const standardCastingTimes = ['action', 'bonus', 'reaction'];

const matchesTime = (time, castingTimeFilter) => {
    if (castingTimeFilter === 'OTHER') {
        return !standardCastingTimes.includes(time.unit);
    }
    else {
        return time.unit.localeCompare(castingTimeFilter.toLowerCase()) === 0;
    }
}

export const filterByCastingTime = ({ time }, castingTimeFilter) => {
    if (!castingTimeFilter) return true;

    return time.some(t => matchesTime(t, castingTimeFilter));
};

export const canUpcast = (spell) => {
    return spell.hasOwnProperty('entriesHigherLevel');
};

export const removeMacros = (t) => (
    t.replaceAll(/{@dice (.*?)}/g, "$1")
    .replaceAll(/{@damage (.*?)}/g, "$1")
    .replaceAll(/{@scaledamage (.*)\|(.*?)}/g, "$2")
    .replaceAll(/{@scaledice (.*?)(\|.*?)?}/g, "$1")
    .replaceAll(/{@i (.*?)}/g, "\"$1\"")
    .replaceAll(/{@skill (.*?)}/g, "$1")
    .replaceAll(/{@creature (.*?)(\|.*?)?}/g, "$1")
    .replaceAll(/{@item (.*?)\|.*?}/g, "$1")
    .replaceAll(/{@filter (.*?)\|.*?}/g, "$1")
    .replaceAll(/{@book (.*?)\|.*?}/g, "$1")
    .replaceAll(/{@chance (.*?)\|.*?}/g, "$1")
    .replaceAll(/{@note (.*)}/g, "$1")
    .replaceAll(/{@condition (.*?)(\|.*?)?}/g, "$1")
    .replaceAll(/{@classFeature (.*?)(\|.*?)?}/g, "$1")
    .replaceAll(/{@action (.*?)}/g, "$1")
    .replaceAll(/{@spell (.*?)}/g, "$1")
    .replaceAll(/{@race (.*)\|(.*?)}/g, "$2")
    .replaceAll(/{@race (.*?)}/g, "$1")
    .replaceAll(/{@sense (.*?)}/g, "$1")
    .replaceAll(/{@d20 (.*?)}/g, "+$1")
    .replaceAll(/{@chance (.*?)}/g, "$1%")
);